import React, {useEffect, useMemo, useState} from 'react';
import {Dropdown} from 'primereact/dropdown';
import {Message} from 'primereact/message';

import {Loading} from '../../utils/InlineComponents';
import {isAdmin} from '../../utils/Utils';
import {MarketplaceProductService} from '../../service/MarketplaceProductService';

// The cadences the dispatcher honors (product/marketplace_api/
// service_marketplace_campaign.py ALLOWED_REFRESH_INTERVALS). The scheduler
// tick that runs dispatch_due_campaigns is 1 minute, so 1 minute is the floor.
// Sub-10-minute values multiply marketplace API calls (Trendyol's own-product
// fetch is one request per barcode) and can hit provider rate limits -- use
// them only for small catalogs.
export const REFRESH_INTERVAL_OPTIONS = [1, 3, 5, 10, 15, 30, 60, 120, 240, 360, 720, 1440];
const DEFAULT_REFRESH_INTERVAL_MINUTES = 60;

// `frequency.<minutes>` covers the fixed dropdown presets (REFRESH_INTERVAL_OPTIONS)
// with hand-phrased text ("every hour", "once a day"...). freshness_minutes
// (Task 11/12) is a computed value and can land outside that set (e.g. a
// large Amazon catalog needing 3 cycles at a 15-minute cadence -> 45), so the
// fallback goes through frequencyGeneric's interpolation instead of a
// hardcoded English string, which would otherwise leak into the Turkish UI.
const frequencyLabel = (t, minutes) => t(`onboarding.marketplacePriceCheck.frequency.${minutes}`,
    t('onboarding.marketplacePriceCheck.frequencyGeneric', `every ${minutes} minutes`, {count: minutes}));

// The settle-floor overrides the backend accepts (campaign/views_campaign.py
// marketplace_settle_interval / MarketplaceCampaignService.set_settle_min_minutes).
// `null` clears the override and restores the shipped default below -- it is a
// real, selectable option here, not just "unset".
export const SETTLE_INTERVAL_OPTIONS = [5, 10, 15, 20, 30, 45, 60, 90, 120];
const DEFAULT_SETTLE_MINUTES = 15;

const settleOptionLabel = (t, minutes) => minutes === null
    ? t('onboarding.marketplacePriceCheck.settleWait.defaultOption', `Default (${DEFAULT_SETTLE_MINUTES} min)`)
    : t(`onboarding.marketplacePriceCheck.settleWait.option.${minutes}`, `${minutes} minutes`);

// Onboarding step for marketplace_price_check (Task 19), inserted between
// product selection and dynamic pricing (see onboardingConfig.js
// stepsForUsageType). Mirrors MarketplaceProductsStep's summary-panel visual
// style: a green "here's the current state" panel when connected, a muted
// hint otherwise. The current cadence is read-only for everyone; only admins
// get the control to change it -- see service_marketplace_campaign.py
// set_refresh_interval and the admin-guarded endpoint in views_campaign.py.
const MarketplacePriceCheckStep = ({t, campaignId}) => {
    const service = useMemo(() => new MarketplaceProductService(), []);
    const [integration, setIntegration] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [feedback, setFeedback] = useState(null); // {severity, text}
    const [savingSettle, setSavingSettle] = useState(false);
    const [settleFeedback, setSettleFeedback] = useState(null); // {severity, text}

    useEffect(() => {
        if (!campaignId) return;
        setLoading(true);
        service.getIntegration(campaignId)
            .then(setIntegration)
            .catch(() => setIntegration(null))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignId]);

    const connected = !!integration?.marketplace;
    const minutes = integration?.refresh_interval_minutes || DEFAULT_REFRESH_INTERVAL_MINUTES;
    // What the marketplace's rate limit actually allows (Task 11's
    // freshness_minutes), as opposed to `minutes` above which is just the
    // configured cadence. Equal for Trendyol/Hepsiburada always, and for
    // Amazon whenever its catalog fits inside one cycle -- see
    // get_integration_summary in service_marketplace_campaign.py. Only shown
    // when it differs (below); repeating the same number would be noise.
    const freshnessMinutes = integration?.freshness_minutes;
    // `null` (no override set) is a valid, distinct value from any of the
    // whitelisted minute counts -- it must round-trip back to the "default"
    // option, not fall through to a hidden 15.
    const settleMinutes = integration?.settle_min_minutes ?? null;
    // Products the sweep excludes every cycle for having no marketplace
    // identifier to look offers up by (Amazon only -- a missing ASIN; see
    // get_integration_summary in service_marketplace_campaign.py). Always 0
    // for Trendyol/Hepsiburada. Without this line those products silently
    // never update and the customer has no way to find out why.
    const untrackableCount = integration?.untrackable_product_count || 0;

    const onChangeInterval = (value) => {
        if (!value || value === minutes) return;
        setSaving(true);
        setFeedback(null);
        service.setRefreshInterval(campaignId, value).then(() => {
            // Refetch rather than patch refresh_interval_minutes locally --
            // freshness_minutes depends on the cadence (cycle_budget), so a
            // local patch would leave the effective-observation-period line
            // showing a stale figure until the next full reload.
            return service.getIntegration(campaignId).then(setIntegration);
        }).then(() => {
            setFeedback({severity: 'success',
                text: t('onboarding.marketplacePriceCheck.saveSuccess', 'Refresh frequency updated.')});
        }).catch(err => {
            const data = err && err.response && err.response.data;
            setFeedback({severity: 'error', text: (data && (data.error || data.detail)) || t('api.detail.500')});
        }).finally(() => setSaving(false));
    };

    const onChangeSettleInterval = (value) => {
        if (value === settleMinutes) return;
        setSavingSettle(true);
        setSettleFeedback(null);
        service.setSettleInterval(campaignId, value).then(() => {
            setIntegration(prev => ({...(prev || {}), settle_min_minutes: value}));
            setSettleFeedback({severity: 'success',
                text: t('onboarding.marketplacePriceCheck.settleWait.saveSuccess', 'Settle wait updated.')});
        }).catch(err => {
            const data = err && err.response && err.response.data;
            setSettleFeedback({severity: 'error', text: (data && (data.error || data.detail)) || t('api.detail.500')});
        }).finally(() => setSavingSettle(false));
    };

    return (
        <div>
            {loading
                ? <div style={{textAlign: 'center', padding: '2em 1em'}}>
                    <Loading/>
                    <div style={{marginTop: 12, color: '#6b7280'}}>{t('onboarding.loading', 'Loading…')}</div>
                  </div>
                : connected
                ? <div style={{border: '1px solid #c7e3d4', background: '#f0fdf4', borderRadius: 10, padding: 16, marginBottom: 16}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                        <span style={{
                            width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                            background: '#16a34a', color: '#fff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                            <i className="pi pi-clock"/>
                        </span>
                        <span style={{fontWeight: 700, color: '#111827'}}>
                            {t('onboarding.marketplacePriceCheck.currentFrequencyLabel', 'Current refresh frequency')}
                            {': '}{frequencyLabel(t, minutes)}
                        </span>
                    </div>
                    {/* Only rendered when the rate limit can't keep up with the configured
                        cadence (large Amazon catalogs) -- see freshnessMinutes above. For
                        Trendyol/Hepsiburada and small Amazon catalogs this is always equal
                        to `minutes`, so an always-repeating line would just be noise. */}
                    {freshnessMinutes != null && freshnessMinutes !== minutes &&
                        <div style={{color: '#4b5563', marginTop: 6, fontSize: '0.9rem'}}>
                            {t('onboarding.marketplacePriceCheck.effectiveFrequencyLabel', 'Effective observation period')}
                            {': '}{frequencyLabel(t, freshnessMinutes)}
                        </div>}
                    {/* Only rendered when non-zero -- these products are otherwise
                        invisible: no error, no log, no counter the customer can see.
                        Always 0 for Trendyol/Hepsiburada (see untrackableCount above). */}
                    {untrackableCount > 0 &&
                        <div style={{marginTop: 10}}>
                            <Message severity="warn" text={t('onboarding.marketplacePriceCheck.untrackableWarning',
                                '{{count}} product(s) can\'t be tracked: no Amazon ASIN is stored for them.',
                                {count: untrackableCount})}/>
                        </div>}
                    <div style={{color: '#4b5563', marginTop: 10, fontSize: '0.9rem'}}>
                        {t('onboarding.marketplacePriceCheck.note',
                            'Buybox prices are pulled from the marketplace API on this cadence.')}
                    </div>

                    {isAdmin() &&
                        <div style={{marginTop: 16, paddingTop: 16, borderTop: '1px solid #c7e3d4'}}>
                            <label style={{display: 'block', fontSize: '.8rem', color: '#6b7280', marginBottom: 6}}>
                                {t('onboarding.marketplacePriceCheck.adminControlLabel', 'Change refresh frequency (admin)')}
                            </label>
                            <Dropdown value={minutes} disabled={saving} style={{width: 260}}
                                      options={REFRESH_INTERVAL_OPTIONS.map(value => ({value, label: frequencyLabel(t, value)}))}
                                      onChange={(e) => onChangeInterval(e.value)}/>
                            {feedback &&
                                <div style={{marginTop: 10}}>
                                    <Message severity={feedback.severity} text={feedback.text}/>
                                </div>}
                        </div>}

                    {isAdmin() &&
                        <div style={{marginTop: 16, paddingTop: 16, borderTop: '1px solid #c7e3d4'}}>
                            <label style={{display: 'block', fontSize: '.8rem', color: '#6b7280', marginBottom: 6}}>
                                {t('onboarding.marketplacePriceCheck.settleWait.controlLabel', 'Settle wait (minutes)')}
                            </label>
                            <Dropdown value={settleMinutes} disabled={savingSettle} style={{width: 260}}
                                      options={[{value: null, label: settleOptionLabel(t, null)}]
                                          .concat(SETTLE_INTERVAL_OPTIONS.map(value => ({value, label: settleOptionLabel(t, value)})))}
                                      onChange={(e) => onChangeSettleInterval(e.value)}/>
                            <div style={{color: '#6b7280', fontSize: '.8rem', marginTop: 8}}>
                                {t('onboarding.marketplacePriceCheck.settleWait.help',
                                    'How long to wait after a price goes live on the marketplace before trusting the buybox rank again. Leave on default unless products are getting stuck waiting.')}
                            </div>
                            {settleFeedback &&
                                <div style={{marginTop: 10}}>
                                    <Message severity={settleFeedback.severity} text={settleFeedback.text}/>
                                </div>}
                        </div>}
                  </div>
                : <Message severity="info" text={t('onboarding.marketplacePriceCheck.notConnected',
                    'Connect on the Integration step first.')}/>}
        </div>
    );
};

export default MarketplacePriceCheckStep;
